import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Input } from '@components';
import { AddBlockedNumberRequest } from '../types';

interface AddBlockedNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: AddBlockedNumberRequest) => Promise<{ success: boolean; error?: string }>;
}

export const AddBlockedNumberModal: React.FC<AddBlockedNumberModalProps> = ({
  isOpen,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation();
  const [number, setNumber] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!number.trim()) {
      setError(t('blockedNumbers.numberRequired'));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await onAdd({ number: number.trim(), reason: reason.trim() || undefined });
      if (result.success) {
        setNumber('');
        setReason('');
        onClose();
      } else {
        setError(result.error || t('blockedNumbers.addError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setNumber('');
      setReason('');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('blockedNumbers.addBlockedNumber')} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('blockedNumbers.phoneNumber')} *
          </label>
          <Input
            type="text"
            value={number}
            onChange={e => {
              setNumber(e.target.value);
              setError(null);
            }}
            placeholder="+49123456789"
            disabled={loading}
          />
          {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('blockedNumbers.reason')} ({t('common.optional')})
          </label>
          <Input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t('blockedNumbers.reasonPlaceholder')}
            disabled={loading}
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            {t('blockedNumbers.blockNumber')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
